import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Monitor, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const pick = (ua, list) => list.find(([re]) => re.test(ua))?.[1] || "";
export function deviceName(ua = "") {
  const browser = pick(ua, [[/Edg\//, "Edge"], [/OPR\/|Opera/, "Opera"], [/Firefox\//, "Firefox"], [/Chrome\//, "Chrome"], [/Safari\//, "Safari"]]) || "Browser";
  const os = pick(ua, [[/iPhone|iPad/, "iPhone/iPad"], [/Android/, "Android"], [/Windows/, "Windows"], [/Mac OS X|Macintosh/, "Mac"], [/CrOS/, "Chromebook"], [/Linux/, "Linux"]]) || "Unknown device";
  return { label: `${browser} on ${os}`, mobile: /iPhone|iPad|Android|Mobile/.test(ua) };
}

const when = (iso) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 10) return "Active now";
  if (mins < 60) return `Active ${mins} min ago`;
  return `Last active ${fmtDate(iso)} ${new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
};

export function SessionsDialog({ open, onClose }) {
  const { logout } = useApp();
  const [rows, setRows] = useState(null);
  const [sure, setSure] = useState(false);
  useEffect(() => {
    if (open) { setSure(false); setRows(null); api.get("/auth/sessions").then((r) => setRows(r.data)).catch((e) => { toast.error(errMsg(e)); setRows([]); }); }
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sessions-dialog">
        <DialogHeader>
          <DialogTitle>Signed-in devices</DialogTitle>
          <DialogDescription>Devices and browsers where you are signed in (last 7 days).</DialogDescription>
        </DialogHeader>
        <div className="max-h-80 space-y-2 overflow-y-auto" data-testid="sessions-list">
          {rows === null && <p className="py-6 text-center text-sm text-slate-500">Loading...</p>}
          {rows?.length === 0 && <p className="py-6 text-center text-sm text-slate-500">No signed-in devices found.</p>}
          {rows?.map((s) => {
            const d = deviceName(s.user_agent);
            const Icon = d.mobile ? Smartphone : Monitor;
            return (
              <div key={s.id} className={`flex items-center gap-3 rounded-lg border p-3 ${s.current ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`} data-testid={`session-row-${s.id}`}>
                <Icon className="h-5 w-5 shrink-0 text-slate-600" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900">{d.label}{s.current && <span className="ml-2 rounded-full bg-emerald-600 px-2 py-0.5 text-xs text-white" data-testid="session-current-badge">This device</span>}</div>
                  <div className="text-xs text-slate-500">{when(s.last_seen)} · Signed in {fmtDate(s.created_at)}{s.ip ? ` · IP ${s.ip}` : ""}</div>
                </div>
              </div>
            );
          })}
        </div>
        {sure && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-800" data-testid="sign-out-confirm-note">This signs you out on every device, including this one. Press the red button again to confirm.</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="sessions-close-btn">Close</Button>
          <Button className="bg-red-700 hover:bg-red-800" onClick={() => (sure ? logout() : setSure(true))} data-testid="sign-out-everywhere-btn"><LogOut className="mr-2 h-4 w-4" /> {sure ? "Yes, sign out all devices" : "Sign out everywhere"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
